import { Box, Button, Grid, makeStyles, Typography } from "@material-ui/core";
import { MeetingRoom, Schedule } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { observer } from "mobx-react-lite";
import useTranslation from "next-translate/useTranslation";
import { useMemo } from "react";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
  },
  overviewContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  legendContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
  },
  legendRectangle: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(1),
    borderRadius: 4,
  },
  overviewCard: {
    padding: theme.spacing(2),
    height: "100%",
    backgroundColor: theme.palette.action.hover,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  outerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  gridContainer: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(8, 1fr)",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(6, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  active_vacant: {
    backgroundColor: "#38b000",
  },
  active_occupied: {
    backgroundColor: "#e5383b",
  },
  inactive: {
    backgroundColor: "#adb5bd",
  },
  scheduled_disable: {
    backgroundColor: "#fb8c00",
  },
  propertySection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  propertyHeading: {
    marginBottom: theme.spacing(1.5),
  },
  toggleButtonLabel: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
  },
  instructionAlert: {
    marginBottom: theme.spacing(2),
    borderRadius: 10,
  },
}));

/**
 * RoomToggleButton — visual toggle for a single room.
 * Keeps rooms in one of three states: active-vacant, active-occupied, inactive.
 * When scheduled for disable, shown in amber.
 */
function RoomToggleButton({ titleLabel, label, enabled, vacant, scheduledDisable, onToggle, disabledReason }) {
  const classes = useStyles();
  const isOccupied = !vacant;
  const isInactive = !enabled;
  const isDisabled = !!disabledReason;

  let borderColor = isInactive ? "#adb5bd" : isOccupied ? "#e5383b" : "#38b000";
  let bgColor = isInactive ? "#adb5bd" : "#fff";
  let textColor = isInactive ? "#eee" : isOccupied ? "#e5383b" : "#38b000";

  if (scheduledDisable && enabled) {
    // Enabled but marked for future disable → amber
    borderColor = "#fb8c00";
    bgColor = "rgba(251, 140, 0, 0.06)";
    textColor = "#fb8c00";
  }

  return (
    <Button
      type="button"
      variant="outlined"
      fullWidth
      onClick={onToggle}
      disabled={isDisabled && !enabled}
      title={disabledReason || (titleLabel ? `${titleLabel} — ${label}` : label)}
      style={{
        display: "flex",
        padding: 8,
        minHeight: 72,
        borderRadius: 12,
        borderWidth: 2,
        borderStyle: "solid",
        borderColor,
        backgroundColor: bgColor,
        color: textColor,
        textTransform: "none",
        flexDirection: "column",
        gap: 4,
        alignItems: "flex-start",
        justifyContent: "center",
        position: "relative",
        opacity: isDisabled && !enabled ? 0.5 : 1,
        transition: "all 0.18s ease",
      }}
      classes={{ label: classes.toggleButtonLabel }}
    >
      <span style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
        {scheduledDisable && enabled ? <Schedule fontSize="small" /> : <MeetingRoom fontSize="small" />}
        <Typography variant="caption" component="span" style={{ lineHeight: 1.2, fontWeight: 600 }}>
          {label}
        </Typography>
      </span>
      {scheduledDisable && enabled && (
        <Typography variant="caption" style={{ fontSize: "0.6rem", color: "#fb8c00", fontWeight: 700 }}>
          Disable on date
        </Typography>
      )}
      {isDisabled && !enabled && (
        <Typography variant="caption" style={{ fontSize: "0.6rem", color: "#f44336", fontWeight: 700 }}>
          {disabledReason}
        </Typography>
      )}
    </Button>
  );
}

/**
 * DelayedRoomSelector
 *
 * A controlled component (NO Formik) used inside ManageSubscriptionForm's Step 4.
 * The user marks which rooms will be disabled on the deferred effective date.
 *
 * Props:
 *   - rooms: [{ id, propertyId, name, enabled, vacant }]  (current live state)
 *   - properties: [{ id, name }]
 *   - newLimit: number  — the new purchased room cap the user is downsizing to
 *   - value: [{ id, propertyId, name, enabled, vacant }]  — controlled selection state (null = uninitialized)
 *   - onChange: (rooms) => void  — called on each toggle, passes full rooms array
 */
const DelayedRoomSelector = observer(({ rooms = [], properties = [], newLimit = 0, value, onChange }) => {
  const { t } = useTranslation("common");
  const classes = useStyles();

  // Controlled rooms state — comes from parent
  const controlledRooms = value || rooms.map((r) => ({ ...r }));

  const enabledCount = useMemo(() => controlledRooms.filter((r) => r.enabled).length, [controlledRooms]);
  const remainingToDisable = enabledCount - newLimit;
  const selectionValid = enabledCount <= newLimit;

  const handleToggle = (roomId) => {
    const updated = controlledRooms.map((r) => {
      if (r.id !== roomId) return r;
      // Prevent enabling beyond newLimit
      if (!r.enabled && enabledCount >= newLimit) return r;
      return { ...r, enabled: !r.enabled };
    });
    onChange(updated);
  };

  return (
    <Box>
      {/* Instruction alert */}
      {!selectionValid ? (
        <Alert severity="warning" variant="outlined" className={classes.instructionAlert}>
          <strong>{t("Select {{count}} more room(s) to disable.", { count: remainingToDisable })}</strong>{" "}
          {t(
            "These rooms will remain active until the effective date. Occupied rooms can still be selected — ensure tenants vacate before that date.",
          )}
        </Alert>
      ) : (
        <Alert severity="success" variant="outlined" className={classes.instructionAlert}>
          {t("Room selection complete. {{count}} room(s) scheduled for disable on the effective date.", {
            count: controlledRooms.filter((r) => !r.enabled).length,
          })}
        </Alert>
      )}

      {/* Overview Cards */}
      <Box mb={3}>
        <Box mb={2} className={classes.overviewContainer}>
          <Box>
            <Typography variant="subtitle1" component="h2" className={classes.sectionTitle}>
              {t("Room List")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("Select the rooms that will be disabled when the downsize takes effect.")}
            </Typography>
          </Box>
          <Box className={classes.legendContainer}>
            <Box className={classes.legendItem}>
              <Box className={`${classes.legendRectangle} ${classes.active_vacant}`} />
              <Typography variant="body2">{t("Active Vacant")}</Typography>
            </Box>
            <Box className={classes.legendItem}>
              <Box className={`${classes.legendRectangle} ${classes.active_occupied}`} />
              <Typography variant="body2">{t("Active Occupied")}</Typography>
            </Box>
            {/* <Box className={classes.legendItem}>
              <Box className={`${classes.legendRectangle} ${classes.scheduled_disable}`} />
              <Typography variant="body2">{t("Scheduled Disable")}</Typography>
            </Box> */}
            <Box className={classes.legendItem}>
              <Box className={`${classes.legendRectangle} ${classes.inactive}`} />
              <Typography variant="body2">{t("Inactive")}</Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box className={classes.overviewCard}>
              <Typography variant="caption" color="textSecondary" style={{ textTransform: "uppercase" }}>
                {t("New Limit")}
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 800, color: "#1976d2" }}>
                {newLimit}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.overviewCard}>
              <Typography variant="caption" color="textSecondary" style={{ textTransform: "uppercase" }}>
                {t("Currently Enabled")}
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 800, color: !selectionValid ? "#f44336" : "inherit" }}>
                {enabledCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.overviewCard}>
              <Typography variant="caption" color="textSecondary" style={{ textTransform: "uppercase" }}>
                {t("Still to Disable")}
              </Typography>
              <Typography
                variant="h5"
                style={{
                  fontWeight: 800,
                  color: remainingToDisable > 0 ? "#fb8c00" : "#38b000",
                }}
              >
                {Math.max(0, remainingToDisable)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Room grid grouped by property */}
      <Box className={classes.outerContainer}>
        {properties.map((property) => {
          const propertyRooms = controlledRooms
            .map((room) => ({ room }))
            .filter(({ room }) => room.propertyId === property.id);

          if (propertyRooms.length === 0) return null;

          return (
            <Box key={property.id} className={classes.propertySection}>
              <Box className={classes.propertyHeading}>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  {property.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("{{count}} rooms", { count: propertyRooms.length })}
                </Typography>
              </Box>
              <Box className={classes.gridContainer}>
                {propertyRooms.map(({ room }) => {
                  // Prevent re-enabling past the new limit
                  const canEnable = enabledCount < newLimit;
                  const disabledReason = !room.enabled && !canEnable ? t("Limit reached") : null;
                  // A room is "scheduled for disable" if it's currently enabled but user toggled it off
                  const originalRoom = rooms.find((r) => r.id === room.id);
                  const scheduledDisable = originalRoom?.enabled && !room.enabled;
                  // Also highlight enabled rooms that are STILL enabled but will need to be disabled
                  const stillEnabled = room.enabled;

                  return (
                    <RoomToggleButton
                      key={room.id}
                      titleLabel={property.name}
                      label={room.name}
                      enabled={room.enabled}
                      vacant={room.vacant}
                      scheduledDisable={!room.enabled && originalRoom?.enabled}
                      disabledReason={disabledReason}
                      onToggle={() => handleToggle(room.id)}
                    />
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

export default DelayedRoomSelector;
